import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { UserGroupDTO } from '../../../dto/user-group/user-group.dto';
import { UserGroupMemberDTO } from '../../../dto/user-group/user-group-member.dto';

export interface DunaUserGroupsMembersDialogData {
  group: UserGroupDTO;
  locationGuid?: string | null;
}

export const DUNA_USER_GROUPS_MEMBERS_DIALOG_CONFIG = {
  width: '92vw',
  minWidth: '1100px',
  maxWidth: '1400px',
  maxHeight: '90vh',
};

@Component({
  selector: 'app-duna-user-groups-members-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  template: `
    <h2 mat-dialog-title>Administrer brugere – {{ data.group.name }}</h2>
    <mat-dialog-content>
      @if (loading) {
      <p class="loading-row">
        <mat-progress-spinner
          diameter="28"
          mode="indeterminate"
        ></mat-progress-spinner>
        Henter brugere…
      </p>
      } @if (!loading) {
      <div class="dual-panel">
        <div class="panel">
          <h3>Tilgængelige</h3>
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Søg</mat-label>
            <input matInput [(ngModel)]="availableSearch" />
          </mat-form-field>
          <ul class="user-list">
            @for (u of filteredAvailable; track u) {
            <li (click)="assign(u)" class="clickable">
              <span>{{ u.name }}</span>
              <mat-icon>add_circle_outline</mat-icon>
            </li>
            } @if (filteredAvailable.length === 0) {
            <li class="empty">Ingen brugere</li>
            }
          </ul>
        </div>
        <div class="panel assigned">
          <h3>Tildelte ({{ selected.length }})</h3>
          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Søg</mat-label>
            <input matInput [(ngModel)]="assignedSearch" />
          </mat-form-field>
          <ul class="user-list">
            @for (u of filteredSelected; track u) {
            <li (click)="unassign(u)" class="clickable assigned-row">
              <span>{{ u.name }}</span>
              <mat-icon>remove_circle_outline</mat-icon>
            </li>
            } @if (filteredSelected.length === 0) {
            <li class="empty">Ingen tildelte brugere</li>
            }
          </ul>
        </div>
      </div>
      } @if (error) {
      <p class="error">{{ error }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button
        mat-stroked-button
        (click)="dialogRef.close(false)"
        [disabled]="saving"
      >
        Annuller
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="save()"
        [disabled]="loading || saving"
      >
        @if (saving) {
        <mat-progress-spinner
          diameter="18"
          mode="indeterminate"
          class="inline-spinner"
        ></mat-progress-spinner>
        } Gem
      </button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      mat-dialog-content {
        min-width: 1040px;
        width: 100%;
        max-height: 75vh;
        box-sizing: border-box;
      }
      .dual-panel {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        min-height: 420px;
      }
      .panel {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .panel h3 {
        margin: 0 0 8px;
        font-size: 0.95rem;
        font-weight: 600;
      }
      .search-field {
        width: 100%;
        margin-bottom: 4px;
      }
      .user-list {
        list-style: none;
        margin: 0;
        padding: 0;
        flex: 1;
        min-height: 320px;
        max-height: calc(70vh - 220px);
        overflow-y: auto;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
      }
      .user-list li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 1px solid #f0f0f0;
      }
      .user-list li:last-child {
        border-bottom: none;
      }
      .clickable {
        cursor: pointer;
      }
      .clickable:hover {
        background: #f5f5f5;
      }
      .assigned-row {
        background: #e8f5e9;
      }
      .assigned-row:hover {
        background: #c8e6c9;
      }
      .empty {
        color: #888;
        font-style: italic;
        justify-content: center;
      }
      .loading-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 24px;
      }
      .error {
        color: #f44336;
        margin-top: 12px;
      }
      .inline-spinner {
        display: inline-block;
        vertical-align: middle;
        margin-right: 6px;
      }
    `,
  ],
})
export class DunaUserGroupsMembersDialogComponent implements OnInit {
  loading = true;
  saving = false;
  error: string | null = null;
  available: UserGroupMemberDTO[] = [];
  selected: UserGroupMemberDTO[] = [];
  availableSearch = '';
  assignedSearch = '';

  constructor(
    public dialogRef: MatDialogRef<DunaUserGroupsMembersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DunaUserGroupsMembersDialogData,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadMembers();
  }

  get filteredAvailable(): UserGroupMemberDTO[] {
    const q = this.availableSearch.trim().toLowerCase();
    return this.available.filter(
      (u) =>
        !q ||
        u.name.toLowerCase().includes(q) ||
        (u.user || '').toLowerCase().includes(q)
    );
  }

  get filteredSelected(): UserGroupMemberDTO[] {
    const q = this.assignedSearch.trim().toLowerCase();
    return this.selected.filter(
      (u) =>
        !q ||
        u.name.toLowerCase().includes(q) ||
        (u.user || '').toLowerCase().includes(q)
    );
  }

  private membersUrl(): string {
    const id = this.data.group.group_id;
    if (this.data.locationGuid) {
      return `${environment.apiUrl}/user-groups/${id}/members/by-location/${this.data.locationGuid}`;
    }
    return `${environment.apiUrl}/user-groups/${id}/members`;
  }

  private loadMembers(): void {
    this.http
      .get<{
        data?: {
          members: UserGroupMemberDTO[];
          available_users: UserGroupMemberDTO[];
        };
        members?: UserGroupMemberDTO[];
        available_users?: UserGroupMemberDTO[];
      }>(this.membersUrl())
      .subscribe({
        next: (res) => {
          const payload = (res as any)?.data ?? res;
          this.selected = payload.members || [];
          this.available = payload.available_users || [];
          this.loading = false;
        },
        error: () => {
          this.error = 'Kunne ikke hente brugere.';
          this.loading = false;
        },
      });
  }

  assign(user: UserGroupMemberDTO): void {
    this.selected = [...this.selected, user];
    this.available = this.available.filter((u) => u.uuid !== user.uuid);
  }

  unassign(user: UserGroupMemberDTO): void {
    this.available = [...this.available, user];
    this.selected = this.selected.filter((u) => u.uuid !== user.uuid);
  }

  save(): void {
    this.saving = true;
    this.error = null;
    const body = { user_uuids: this.selected.map((u) => u.uuid) };
    this.http.put(this.membersUrl(), body).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err) => {
        this.saving = false;
        this.error = err?.error?.message || 'Kunne ikke gemme tildelinger.';
      },
    });
  }
}
